import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Confirm } from '../components/Confirm';
import { DataTable } from '../components/DataTable';
import { Pagination } from '../components/Pagination';
import { Badge } from '../components/Badge';
import { HelpTip } from '../components/HelpTip';
import { useToast } from '../components/Toast';
import { useAuth } from '../hooks/useAuth';
import { useDebounce } from '../hooks/useDebounce';
import { useServerPage } from '../hooks/useServerPage';
import { api, extractErrorMessage } from '../api/client';

type EstadoStock = 'reservado' | 'descontado' | 'liberado' | 'full' | 'revisar' | 'anterior';

/** Una venta con impacto en el stock (GET reservas-despachos): sin precios ni costos. */
interface VentaStock {
  id: number;
  fecha: string;
  canal: string;
  estado: string;
  mlOrderId: string | null;
  mlShipmentId: string | null;
  modalidadEnvio: string | null;
  mlEnvioEstado: string | null;
  mlEnvioActualizadoEn: string | null;
  despachadaEn: string | null;
  stockSalidaEn: string | null;
  /** El vendedor la marcó «no entregada» en Mercado Libre (null: no). */
  mlEntregaNegadaEn: string | null;
  estadoStock: EstadoStock;
  items: Array<{ skuInterno: string | null; descripcion: string; cantidad: number; cantidadReservada: number }>;
}

interface ResultadoEnvio {
  ovId: number;
  mlShipmentId: string;
  estadoEnvio: string | null;
  stock: 'descontado' | 'ya_descontado' | 'sin_reserva' | 'no_aplica' | 'sin_cambios' | 'devolucion';
}

const CANALES: Array<{ valor: string; etiqueta: string }> = [
  { valor: 'mercadolibre', etiqueta: 'Mercado Libre' },
  { valor: 'mostrador', etiqueta: 'Mostrador' },
  { valor: '', etiqueta: 'Todos' },
];

type TonoEstadoStock = 'amber' | 'green' | 'slate' | 'blue' | 'red';

/** Etiqueta corta (tabla y filtro) y su explicación en una línea (al pasar el mouse y en la referencia). */
const ESTADO_STOCK: Record<EstadoStock, { etiqueta: string; explicacion: string; tono: TonoEstadoStock }> = {
  reservado: { etiqueta: 'Reservado', explicacion: 'Reservó el stock, todavía en el depósito. Sin envío de Mercado Libre, sale solo cuando ML la marca entregada.', tono: 'amber' },
  descontado: { etiqueta: 'Descontado', explicacion: 'El stock salió del depósito (despacho de Mercado Libre, entrega confirmada en Mercado Libre, entrega en mano o factura), en la fecha indicada.', tono: 'green' },
  liberado: { etiqueta: 'Liberado', explicacion: 'Venta cancelada antes de que saliera el stock: la reserva se liberó.', tono: 'slate' },
  full: { etiqueta: 'Full (sin stock propio)', explicacion: 'Envío Full: la mercadería la tiene Mercado Libre; no reserva ni descuenta el depósito propio.', tono: 'blue' },
  revisar: { etiqueta: 'Revisar devolución', explicacion: 'Venta cancelada después de que salió el stock: el stock no se tocó; cuando la mercadería vuelva, recibila en Día a día → Stock → Devoluciones.', tono: 'red' },
  anterior: { etiqueta: 'Venta anterior (sin reserva)', explicacion: 'Confirmada antes de que las ventas reservaran stock: no reserva ni descuenta el depósito.', tono: 'slate' },
};

/** Estado del stock que la pantalla no conoce (p. ej. uno nuevo del backend): neutro, sin mostrar la clave. */
const ESTADO_STOCK_OTRO = { etiqueta: 'Otro', explicacion: 'Estado del stock sin descripción en esta pantalla.', tono: 'slate' as TonoEstadoStock };

/** Estado del envío en Mercado Libre (status de /shipments/{id}). */
const ESTADO_ENVIO: Record<string, string> = {
  pending: 'Pendiente',
  handling: 'En preparación',
  ready_to_ship: 'Listo para despachar',
  shipped: 'En camino (despachado)',
  delivered: 'Entregado',
  not_delivered: 'No entregado',
  cancelled: 'Cancelado',
  to_be_agreed: 'A acordar',
  closed: 'Cerrado',
};

const RESULTADO_STOCK: Record<ResultadoEnvio['stock'], string> = {
  descontado: 'stock descontado del depósito',
  ya_descontado: 'el stock ya había salido',
  sin_reserva: 'sin reserva (anterior al cambio): no se descuenta stock',
  no_aplica: 'no descuenta stock',
  sin_cambios: 'todavía no salió del depósito',
  devolucion: 'devolución esperada (Día a día → Stock → Devoluciones)',
};

const PERMISO_DENEGADO = 'No tenés permiso para realizar esta acción';

/** fecha de la OV (columna date): se muestra el día guardado, sin correrlo por la zona horaria. */
const fmtDia = (iso: string) => new Date(iso).toLocaleDateString('es-AR', { timeZone: 'UTC' });
const fmtFechaHora = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : '—';
const fmtCantidad = (n: number) => Number(n).toLocaleString('es-AR', { maximumFractionDigits: 4 });
/** Lo reservado de la venta, como sale del depósito: `SKU × cantidad, …`. */
const reservadoDe = (v: VentaStock) => v.items
  .filter(it => it.cantidadReservada > 0)
  .map(it => `${it.skuInterno ?? it.descripcion} × ${fmtCantidad(it.cantidadReservada)}`)
  .join(', ');

export function ReservasDespachosPage() {
  const toast = useToast();
  const { can } = useAuth();
  const puedeActualizar = can('ventas', 'editar');

  const [canal, setCanal] = useState('mercadolibre');
  const [estadoStock, setEstadoStock] = useState('');
  const [sku, setSku] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [actualizando, setActualizando] = useState<number | null>(null);
  const skuDebounced = useDebounce(sku.trim(), 400);

  const { data, loading, error, total, refetch } = useServerPage<VentaStock>(
    '/api/v3/reservas-despachos', page, pageSize, '', 'q',
    { canal, estado_stock: estadoStock, sku: skuDebounced, desde, hasta },
    { serverSideFilters: true },
  );

  const cambiarFiltro = (setter: (v: string) => void) => (v: string) => { setter(v); setPage(1); };

  // Entregada en mano: la venta que se confirma (null = cerrada) y un candado contra el doble clic.
  const [enMano, setEnMano] = useState<VentaStock | null>(null);
  const [entregando, setEntregando] = useState(false);
  const entregandoRef = useRef(false);

  const confirmarEnMano = async () => {
    if (!enMano || entregandoRef.current) return;
    entregandoRef.current = true;
    setEntregando(true);
    try {
      await api.post(`/api/v3/ventas/${enMano.id}/entregada-en-mano`, {});
      toast.addToast(`OV #${enMano.id} entregada: salió del depósito.`, 'success');
      refetch();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      entregandoRef.current = false;
      setEntregando(false);
      setEnMano(null);
    }
  };

  const actualizarEnvio = async (v: VentaStock) => {
    setActualizando(v.id);
    try {
      const r = await api.post<ResultadoEnvio>(`/api/v3/ml/ventas/${v.id}/actualizar-envio`, {});
      const envio = r.estadoEnvio ? ESTADO_ENVIO[r.estadoEnvio] ?? r.estadoEnvio : 'Sin estado';
      toast.addToast(`OV #${v.id} · Envío: ${envio} · ${RESULTADO_STOCK[r.stock] ?? r.stock}`, 'success');
      refetch();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setActualizando(null);
    }
  };

  const columns = [
    { key: 'fecha', header: 'Fecha', render: (v: VentaStock) => fmtDia(v.fecha) },
    { key: 'id', header: 'OV #', render: (v: VentaStock) => `OV #${v.id}` },
    { key: 'mlOrderId', header: 'Orden ML', render: (v: VentaStock) => v.mlOrderId ?? '—' },
    { key: 'modalidadEnvio', header: 'Modalidad', render: (v: VentaStock) => v.modalidadEnvio ?? '—' },
    {
      key: 'items',
      header: 'SKU y cantidades',
      render: (v: VentaStock) => (
        <div className="space-y-0.5">
          {v.items.map((it, i) => (
            <div key={i} title={it.descripcion}>{`${it.skuInterno ?? it.descripcion} × ${fmtCantidad(it.cantidad)}`}</div>
          ))}
        </div>
      ),
    },
    {
      key: 'mlEnvioEstado',
      header: 'Estado del envío',
      help: 'Último estado del envío leído de Mercado Libre (por los avisos de ML, la revisión automática cada 15 minutos o "Actualizar envío").',
      render: (v: VentaStock) => (v.mlEnvioEstado ? ESTADO_ENVIO[v.mlEnvioEstado] ?? v.mlEnvioEstado : '—'),
    },
    {
      key: 'estadoStock',
      header: 'Estado del stock',
      render: (v: VentaStock) => {
        const e = ESTADO_STOCK[v.estadoStock] ?? ESTADO_STOCK_OTRO;
        const etiqueta = v.estadoStock === 'descontado' && v.stockSalidaEn ? `${e.etiqueta} (${fmtFechaHora(v.stockSalidaEn)})` : e.etiqueta;
        const estado = v.estadoStock === 'revisar'
          ? (
            <span title={e.explicacion}>
              <Link to={`/devoluciones?ov=${v.id}`} className="hover:opacity-80"><Badge tone={e.tono}>{etiqueta}</Badge></Link>
            </span>
          )
          : <span title={e.explicacion}><Badge tone={e.tono}>{etiqueta}</Badge></span>;
        // Sólo mientras el stock sigue reservado: si ya salió (p. ej. «Entregada en mano») el aviso no sirve.
        if (!v.mlEntregaNegadaEn || v.estadoStock !== 'reservado') return estado;
        return (
          <div className="flex flex-wrap items-center gap-1">
            {estado}
            <span title={`El vendedor la marcó como no entregada en Mercado Libre (visto el ${fmtFechaHora(v.mlEntregaNegadaEn)}). El stock no se toca.`}>
              <Badge tone="red">En Mercado Libre figura como no entregada</Badge>
            </span>
          </div>
        );
      },
    },
    { key: 'despachadaEn', header: 'Despachada en', render: (v: VentaStock) => fmtFechaHora(v.despachadaEn) },
    {
      key: 'acciones',
      header: '',
      render: (v: VentaStock) => {
        const esMl = !!v.mlOrderId;
        const motivo = !puedeActualizar ? PERMISO_DENEGADO : !esMl ? 'Sólo las ventas de Mercado Libre tienen un envío para actualizar' : undefined;
        const puedeEntregarEnMano = puedeActualizar && v.estadoStock === 'reservado' && !v.mlShipmentId;
        return (
          <div className="flex flex-col items-start gap-1">
            <button
              onClick={() => actualizarEnvio(v)}
              disabled={!!motivo || actualizando === v.id}
              title={motivo}
              className="text-brand-600 hover:text-brand-800 text-xs font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {actualizando === v.id ? 'Actualizando…' : 'Actualizar envío'}
            </button>
            {puedeEntregarEnMano && (
              <button
                onClick={() => setEnMano(v)}
                title="La venta se entregó sin envío de Mercado Libre: saca del depósito lo reservado"
                className="text-brand-600 hover:text-brand-800 text-xs font-medium whitespace-nowrap"
              >
                Entregada en mano
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 inline">Reservas y despachos</h1>
        <HelpTip
          tips={[
            'Una fila por venta con impacto en el stock: lo reservado al confirmarla y cuándo salió del depósito.',
            'Las ventas de Mercado Libre descuentan el stock del depósito cuando ML informa el envío como despachado (o al facturarlas, lo que pase primero).',
            'Full: la mercadería la tiene Mercado Libre; la venta no reserva ni descuenta el depósito propio.',
            '"Revisar devolución": la venta se canceló después de que salió el stock. El stock no se tocó; tocá la etiqueta para ver la devolución esperada y recibirla en Día a día → Stock → Devoluciones.',
            '"Actualizar envío" sólo lee el envío en Mercado Libre; nunca escribe en ML.',
            '"Entregada en mano": para las ventas reservadas sin envío de Mercado Libre (p. ej. «Entrega a acordar»); saca del depósito lo reservado. No escribe en ML ni se puede deshacer desde acá.',
          ]}
        />
        <p className="text-slate-500 text-sm mt-1">Reservas de stock de las ventas y su salida del depósito</p>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label htmlFor="rd-canal" className="block text-xs font-medium text-slate-600 mb-1">Canal</label>
            <select id="rd-canal" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={canal} onChange={e => cambiarFiltro(setCanal)(e.target.value)}>
              {CANALES.map(c => <option key={c.valor} value={c.valor}>{c.etiqueta}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="rd-estado" className="block text-xs font-medium text-slate-600 mb-1">Estado del stock</label>
            <select id="rd-estado" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={estadoStock} onChange={e => cambiarFiltro(setEstadoStock)(e.target.value)}>
              <option value="">Todos</option>
              {(Object.keys(ESTADO_STOCK) as EstadoStock[]).map(e => <option key={e} value={e}>{ESTADO_STOCK[e].etiqueta}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="rd-sku" className="block text-xs font-medium text-slate-600 mb-1">SKU</label>
            <input id="rd-sku" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="Buscar SKU" value={sku} onChange={e => cambiarFiltro(setSku)(e.target.value)} />
          </div>
          <div>
            <label htmlFor="rd-desde" className="block text-xs font-medium text-slate-600 mb-1">Desde</label>
            <input id="rd-desde" type="date" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={desde} onChange={e => cambiarFiltro(setDesde)(e.target.value)} />
          </div>
          <div>
            <label htmlFor="rd-hasta" className="block text-xs font-medium text-slate-600 mb-1">Hasta</label>
            <input id="rd-hasta" type="date" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={hasta} onChange={e => cambiarFiltro(setHasta)(e.target.value)} />
          </div>
        </div>
        <ul aria-label="Estados del stock" className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-500">
          {(Object.keys(ESTADO_STOCK) as EstadoStock[]).map(e => (
            <li key={e}>
              <span className="font-medium text-slate-700">{ESTADO_STOCK[e].etiqueta}</span>
              {': '}
              <span>{ESTADO_STOCK[e].explicacion}</span>
            </li>
          ))}
        </ul>
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      <DataTable
        columns={columns}
        data={data}
        loading={loading}
        emptyMessage="Sin ventas para los filtros seleccionados"
        title="Reservas-y-despachos"
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onChange={setPage}
        onPageSizeChange={s => { setPageSize(s); setPage(1); }}
      />

      <Confirm
        open={!!enMano}
        title="Entregada en mano"
        message={enMano ? `¿La OV #${enMano.id} se entregó en mano? Sale del depósito: ${reservadoDe(enMano)}. Esto no se puede deshacer desde acá.` : ''}
        onConfirm={confirmarEnMano}
        onCancel={() => { if (!entregandoRef.current) setEnMano(null); }}
        disabled={entregando}
      />
    </div>
  );
}
